import axiosInstance from '../helpers/api';

interface PDFFillerConfig {
  apiKey?: string;
  clientId?: string;
  clientSecret?: string;
}

interface PDFFillerDocument {
  id: string;
  name: string;
  editor_url: string;
  fillable_pdf_url: string;
  created: string;
}

class PDFFillerService {
  private baseURL = 'https://api.pdffiller.com/v2';
  private config: PDFFillerConfig;

  constructor(config?: PDFFillerConfig) {
    // In production, these would come from environment variables
    this.config = config || {
      apiKey: process.env.REACT_APP_PDFFILLER_API_KEY,
      clientId: process.env.REACT_APP_PDFFILLER_CLIENT_ID,
      clientSecret: process.env.REACT_APP_PDFFILLER_CLIENT_SECRET
    };
  }

  /**
   * Upload a PDF to PDFFiller and get an editable link
   */
  async uploadAndGetEditLink(file: File | Blob, documentName: string): Promise<PDFFillerDocument> {
    try {
      const formData = new FormData();
      formData.append('file', file, documentName);
      formData.append('name', documentName);

      // First, upload to our backend which will proxy to PDFFiller
      const response = await axiosInstance.post('/pdffiller/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      return response.data;
    } catch (error) {
      console.error('Error uploading to PDFFiller:', error);
      throw error;
    }
  }

  /**
   * Get the LinkToFill URL for a document
   */
  async getLinkToFill(documentId: string): Promise<string> {
    try {
      const response = await axiosInstance.post('/pdffiller/linktofill', {
        document_id: documentId,
        access: 'full',
        // Optional: Set callback URL to get notified when document is filled
        callback_url: `${process.env.REACT_APP_API_URL}/pdffiller/callback`
      });

      return response.data.url;
    } catch (error) {
      console.error('Error getting LinkToFill:', error);
      throw error;
    }
  }

  /**
   * Download the filled PDF
   */
  async downloadFilledPDF(documentId: string): Promise<Blob> {
    try {
      const response = await axiosInstance.get(`/pdffiller/download/${documentId}`, {
        responseType: 'blob'
      });

      return response.data;
    } catch (error) {
      console.error('Error downloading filled PDF:', error);
      throw error;
    }
  }

  /**
   * Create fillable fields on a PDF
   */
  async addFillableFields(documentId: string, fields: any[]): Promise<any> {
    try {
      const response = await axiosInstance.post(`/pdffiller/fields/${documentId}`, {
        fields
      });

      return response.data;
    } catch (error) {
      console.error('Error adding fillable fields:', error);
      throw error;
    }
  }

  /**
   * Get embedded editor URL
   */
  getEmbeddedEditorUrl(documentId: string): string {
    // This URL can be used in an iframe
    return `https://www.pdffiller.com/embed/${documentId}`;
  }

  /**
   * Open PDFFiller editor in a new window
   */
  openInEditor(documentId: string): void {
    const editorUrl = `https://www.pdffiller.com/en/edit-document.htm?document_id=${documentId}`;
    window.open(editorUrl, '_blank', 'width=1200,height=800');
  }
}

export default new PDFFillerService();